import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { Tone } from "@/lib/integrations/words";

import styles from "./integrations.module.css";

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

/**
 * A titled box whose parts keep their distance.
 *
 * The body is a gapped column, so a table, a banner and a line of text inside
 * it never sit flush against each other — the reason the old screens looked
 * like text dropped on a page.
 */
export function Panel({
  title,
  note,
  icon,
  actions,
  children,
}: {
  title?: string;
  note?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.panel}>
      {(title || actions) && (
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            {title && (
              <h2 className={styles.panelTitle}>
                {icon}
                {title}
              </h2>
            )}
            {note && <p className={styles.panelNote}>{note}</p>}
          </div>
          {actions && <div className={styles.panelActions}>{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** A gapped column: the gap is the only space between its children. */
export function Column({ children, gap = "md" }: { children: ReactNode; gap?: "sm" | "md" | "lg" }) {
  return <div className={cx(styles.col, gap === "sm" && styles.colTight, gap === "lg" && styles.colLoose)}>{children}</div>;
}

/** Panels side by side, stacking when there is no room. */
export function Cards({ children }: { children: ReactNode }) {
  return <div className={styles.cards}>{children}</div>;
}

export function Tiles({ children }: { children: ReactNode }) {
  return <div className={styles.tiles}>{children}</div>;
}

const TILE_TONE: Record<Tone, string> = {
  neutral: "",
  info: styles.tileInfo,
  good: styles.tileGood,
  warning: styles.tileWarning,
  danger: styles.tileDanger,
};

/**
 * One number that matters, what it counts, and a line under it.
 *
 * A link with `href`, a toggle with `onClick` (then `pressed` says whether it
 * is on), otherwise plain. The tone colours the icon and edge only — the words
 * carry the meaning.
 */
export function Tile({
  label,
  value,
  note,
  icon,
  tone = "neutral",
  href,
  onClick,
  pressed,
  children,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  href?: string;
  onClick?: () => void;
  pressed?: boolean;
  children?: ReactNode;
}) {
  const body = (
    <>
      <span className={styles.tileLabel}>
        {icon}
        {label}
      </span>
      <span className={styles.tileValue}>{value}</span>
      {children}
      {note && <span className={styles.tileNote}>{note}</span>}
    </>
  );
  const className = cx(styles.tile, TILE_TONE[tone], pressed && styles.tileOn);
  if (href) {
    return (
      <Link to={href} className={className}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} aria-pressed={Boolean(pressed)}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}

/**
 * The top of a page about one thing: its name, a line of facts with icons, its
 * actions, and under them the status block and the numbers that matter.
 */
export function Band({
  title,
  meta,
  actions,
  badge,
  tiles,
}: {
  title: string;
  meta: { key: string; icon: ReactNode; content: ReactNode }[];
  actions?: ReactNode;
  badge: ReactNode;
  tiles: ReactNode;
}) {
  return (
    <section className={styles.band} aria-label={title}>
      <div className={styles.bandTop}>
        <div className={styles.bandText}>
          <h1 className={styles.bandTitle}>{title}</h1>
          <ul className={styles.bandMeta}>
            {meta.map((one) => (
              <li key={one.key}>
                {one.icon}
                {one.content}
              </li>
            ))}
          </ul>
        </div>
        {actions && <div className={styles.bandActions}>{actions}</div>}
      </div>
      <div className={styles.bandBody}>
        {badge}
        <div className={styles.tiles}>{tiles}</div>
      </div>
    </section>
  );
}

/** Short facts, one per line, each with an icon; with `labelled`, a label column too. */
export function Facts({
  items,
  labelled = false,
}: {
  items: { key: string; icon: ReactNode; label?: string; content: ReactNode }[];
  labelled?: boolean;
}) {
  return (
    <ul className={cx(styles.facts, labelled && styles.factsLabelled)}>
      {items.map((item) => (
        <li key={item.key} className={styles.fact}>
          <span className={styles.factIcon}>{item.icon}</span>
          {labelled && <span className={styles.factLabel}>{item.label}</span>}
          <span className={styles.factValue}>{item.content}</span>
        </li>
      ))}
    </ul>
  );
}

/** A thin bar for "this much of that", with its words as the label. */
export function Meter({ value, of, label }: { value: number; of: number; label: string }) {
  const share = of > 0 ? Math.min(1, Math.max(0, value / of)) : 0;
  return (
    <span className={styles.meter} role="img" aria-label={label} title={label}>
      <span className={styles.meterFill} style={{ width: `${share * 100}%` }} />
    </span>
  );
}

/** Things on one line, wrapping, with a gap between them. */
export function Inline({ children }: { children: ReactNode }) {
  return <span className={styles.inline}>{children}</span>;
}

/** A quiet line of explanation. */
export function Note({ children }: { children: ReactNode }) {
  return <p className={styles.textNote}>{children}</p>;
}
